import { array } from 'fp-ts';
import { ExpandableBox } from '@optimus-web/helpers';
import { ExpandIcon, Typography, Stack } from '@optimus-web/core';
import { useToggle } from '@mews/framework';
import { ConvertedCurrencyValue } from '@mews-commander/ui';
import { ProductOrder, ProductOrderGroup } from '../../../transformProductOrders/types';
import { ProductOrderLabel } from '../../../../../components';
import { OffsetStack } from './styles';
import { ProductOrderItemRow } from './ProductOrderItemRow';

interface ProductOrderGroupRowProps {
    group: ProductOrderGroup;
    depth?: number;
    isMultiTaxEnvironment: boolean;
    timeUnitKey: Mews.TextKey;
}

export const ProductOrderGroupRow = ({ group, depth = 0, ...other }: ProductOrderGroupRowProps) => {
    const [expanded, toggle] = useToggle();

    const renderProductOrderSubgroups = (subgroup: ProductOrderGroup) => (
        <ProductOrderGroupRow group={subgroup} depth={depth + 1} {...other} />
    );
    const renderProductOrderItemRow = (order: ProductOrder) => (
        <ProductOrderItemRow
            order={order}
            key={order.id}
            isMultiTaxEnvironment={other.isMultiTaxEnvironment}
            timeUnitKey={other.timeUnitKey}
            depth={depth + 1}
        />
    );

    if (array.size(group.subgroups) === 1) {
        return <ProductOrderGroupRow group={group.subgroups[0]} depth={depth} {...other} />;
    }
    if (array.size(group.orders) === 1) {
        return (
            <ProductOrderItemRow
                order={group.orders[0]}
                isMultiTaxEnvironment={other.isMultiTaxEnvironment}
                timeUnitKey={other.timeUnitKey}
                depth={depth}
                isSingleOrderGroup
            />
        );
    }
    return (
        <>
            <OffsetStack offset={depth} distribution="space-between" itemAlignment="center" onClick={toggle}>
                <Stack itemAlignment="center" spacing="100">
                    <ExpandIcon size="small" expanded={expanded} />
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        <ProductOrderLabel
                            productId={group.productId}
                            totalNumberOfProducts={group.totalNumberOfProducts}
                            ageCategoryId={group.ageCategoryId}
                        />
                    </Typography>
                </Stack>
                <ConvertedCurrencyValue amount={group.totalCost} net={other.isMultiTaxEnvironment} />
            </OffsetStack>
            <ExpandableBox expanded={expanded}>
                {array.isNonEmpty(group.subgroups)
                    ? array.map(renderProductOrderSubgroups)(group.subgroups)
                    : array.map(renderProductOrderItemRow)(group.orders)}
            </ExpandableBox>
        </>
    );
};
